import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  AccessLogIcon,
  CancelCrossIcon,
  CopyIcon,
  Dialog,
  DuplicateIcon,
  Header3,
  IconButton,
  LayerContextProvider,
  layers,
  List,
  ListItem,
  ListSeparator,
  NotificationBellIcon,
  RecurringCheckmarkIcon,
  TrashIcon,
} from "@activecollab/components";
import PropTypes from "prop-types";
import { useRecurringTaskContext } from "~/components/Providers/RecurringTask/context";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users.jsx";
import Angie from "~/utils/angie.jsx";
import MoveCopyTaskToProjectContainer from "./MoveCopyTaskToProjectContainer.jsx";
import TaskDuplicateDialog from "./TaskDuplicateDialog.jsx";
import TrashTaskContainer from "./TrashTaskContainer.jsx";
import AccessLogs from "../AccessLogs/AccessLogs.jsx";
import { InsertLinkListItem } from "../InsertLinkListItem/InsertLinkListItem.js";

const TaskOptions = ({
  taskId,
  projectId,
  onTrash,
  showAccessLogsLink,
  canEdit,
  onCopyPermalinkToClipboard,
  onMoveCopyToProject,
  onDuplicate,
  onMakeRecurring,
  recurringTasksEnabled,
  onAddReminder,
}) => {
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showAccessLogModal, setShowAccessLogModal] = useState(false);
  const [showMoveCopyToProjectModal, setShowMoveCopyToProjectModal] = useState(false);
  const { setParams } = useRecurringTaskContext();
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);

  const { t } = useTranslation();

  const handleShowDuplicateModal = useCallback(() => {
    setShowDuplicateModal(true);
  }, []);

  const handleCloseDuplicateDialog = useCallback(() => {
    setShowDuplicateModal(false);
  }, []);

  const handleShowMakeRecurringTaskModal = useCallback(() => {
    setParams({ open: true, projectId, taskId });
    onMakeRecurring();
  }, [onMakeRecurring, projectId, setParams, taskId]);

  const handleShowMoveCopyToProject = useCallback(() => {
    setShowMoveCopyToProjectModal(true);
  }, []);

  const handleCloseMoveCopyToProject = useCallback(() => {
    setShowMoveCopyToProjectModal(false);
  }, []);

  const handleShowAccessLog = useCallback(() => {
    setShowAccessLogModal(true);
  }, []);

  const handleCloseAccessLog = useCallback(() => {
    setShowAccessLogModal(false);
  }, []);

  const handleAddReminder = useCallback(() => {
    if (typeof onAddReminder === "function") {
      onAddReminder();
    }
  }, [onAddReminder]);

  return (
    <>
      <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
        <List className="tw-py-2">
          {canEdit ? (
            <>
              <ListItem onClick={handleShowDuplicateModal}>
                <DuplicateIcon className="tw-fill-theme-900 tw-shrink-0" /> {t("Duplicate")}
              </ListItem>
              {recurringTasksEnabled ? (
                <ListItem onClick={handleShowMakeRecurringTaskModal}>
                  <RecurringCheckmarkIcon className="tw-fill-theme-900 tw-shrink-0" />
                  {t("Create Recurring Task")}
                </ListItem>
              ) : null}
              <ListItem onClick={handleShowMoveCopyToProject}>
                <CopyIcon className="tw-fill-theme-900 tw-shrink-0" /> {t("Move/Copy to Project")}
              </ListItem>
              <ListSeparator />
            </>
          ) : null}
          <InsertLinkListItem
            link={`${Angie.root_url}/projects/${projectId}/tasks/${taskId}`}
            onCopy={onCopyPermalinkToClipboard}
          />
          {showAccessLogsLink ? (
            <ListItem onClick={handleShowAccessLog}>
              <AccessLogIcon className="tw-fill-theme-900 tw-shrink-0" />
              {t("Access Logs")}
            </ListItem>
          ) : null}
          {!isLoggedUserClient ? (
            <ListItem onClick={handleAddReminder}>
              <NotificationBellIcon className="tw-fill-theme-900 tw-shrink-0" />
              {t("Add Reminder")}
            </ListItem>
          ) : null}
          {canEdit ? (
            <>
              <ListSeparator />
              <TrashTaskContainer
                taskId={taskId}
                onClick={onTrash}
              >
                <ListItem>
                  <TrashIcon className="tw-fill-theme-900 tw-shrink-0" />
                  {t("Delete")}
                </ListItem>
              </TrashTaskContainer>
            </>
          ) : null}
        </List>

        <TaskDuplicateDialog
          open={showDuplicateModal}
          taskId={taskId}
          projectId={projectId}
          onCancel={handleCloseDuplicateDialog}
          onSubmit={handleCloseDuplicateDialog}
          onSuccess={onDuplicate}
        />
        <MoveCopyTaskToProjectContainer
          open={showMoveCopyToProjectModal}
          taskId={taskId}
          projectId={projectId}
          onCancel={handleCloseMoveCopyToProject}
          onSubmit={handleCloseMoveCopyToProject}
          onSuccess={onMoveCopyToProject}
        />
        <Dialog
          open={showAccessLogModal}
          onClose={handleCloseAccessLog}
        >
          <Dialog.Title
            disableDefaultHeading
            className="tw-flex tw-justify-between tw-items-center"
          >
            <Header3>{t("Access Logs")}</Header3>
            <IconButton
              onClick={handleCloseAccessLog}
              variant="text gray"
            >
              <CancelCrossIcon />
            </IconButton>
          </Dialog.Title>
          <Dialog.ContentDivider />
          <Dialog.Content>
            <AccessLogs
              objectId={taskId}
              objectType="Task"
            />
          </Dialog.Content>
        </Dialog>
      </LayerContextProvider>
    </>
  );
};

TaskOptions.propTypes = {
  taskId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  onMakeRecurring: PropTypes.func,
  onCopyPermalinkToClipboard: PropTypes.func,
  onTrash: PropTypes.func,
  showAccessLogsLink: PropTypes.bool,
  canEdit: PropTypes.bool,
  onMoveCopyToProject: PropTypes.func,
  onDuplicate: PropTypes.func,
  recurringTasksEnabled: PropTypes.bool,
  onAddReminder: PropTypes.func,
};

TaskOptions.defaultProps = {
  showAccessLogsLink: false,
  canEdit: false,
  recurringTasksEnabled: true,
};

export default TaskOptions;
